/**
 * @file OrdersPagination.tsx
 * @description Постраничный вывод реестра заказов (phase19.31, макет (а)):
 * «1–50 из 312» · ‹ › · «По 50 ▾».
 *
 * Свой компонент, а не общий `ListPagination`: у того подписи про товары и
 * ручные высоты; здесь — стандартные кнопки темы (компактные 32 на широком
 * экране, 48 на телефоне) и `SelectField`. Страница и размер живут в адресе
 * (`order-registry-query`), компонент только сообщает, что выбрали.
 */

import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "../../ui/button";
import { SelectField } from "../../ui/SelectField";
import { ORDERS_PAGE_SIZES, ordersPageRange } from "../../../lib/order-registry-query";

const SIZE_OPTIONS = ORDERS_PAGE_SIZES.map((size) => ({ value: String(size), label: `По ${size}` }));

interface Props {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export function OrdersPagination({ page, pageSize, total, onPageChange, onPageSizeChange }: Props) {
  const { from, to, pages } = ordersPageRange(page, pageSize, total);
  if (total === 0) return null;
  return (
    <nav aria-label="Страницы реестра заказов" className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <span className="type-body tabular-nums text-on-surface-variant">
        {from}–{to} из {total}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Предыдущая страница"
        >
          <ChevronLeft className="size-4" />
        </Button>
        <span className="type-body min-w-12 text-center tabular-nums" aria-current="page">
          {page} из {pages}
        </span>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pages}
          aria-label="Следующая страница"
        >
          <ChevronRight className="size-4" />
        </Button>
        <SelectField
          ariaLabel="Заказов на странице"
          value={String(pageSize)}
          onChange={(value) => onPageSizeChange(Number(value))}
          options={SIZE_OPTIONS}
          className="w-[120px]"
        />
      </div>
    </nav>
  );
}
